import {
  CSSProperties, memo, ReactElement, useCallback, useEffect, useMemo,
} from 'react';
import { createUseStyles } from 'react-jss';

import { IPageContent as IPageContentData } from 'app/store/nodes/card/interfaces';
import { useDispatcher } from 'app/store/utils/redux/hooks';
import { useMediaResource } from 'app/lib/MediaHooks';
import Video from 'app/lib/Video';
import { useWidgetPointerStyle } from 'hooks';

import ButtonWidget from 'components/ButtonWidget';

const defaultProps = {
  data: null as IPageContentData | null,
};

type PageContentProps = {
  data?: IPageContentData | null,
} & typeof defaultProps;

const PageContent = (props: PageContentProps): ReactElement => {
  const {
    data,
  } = props;

  const dispatcher = useDispatcher();
  const classes = useStyles();

  const handleWaiting = useCallback(() => {
    document.body.setAttribute('data-page-content-video', 'loading');
  }, []);

  const handleTimeUpdate = useCallback(() => {
    if (document.body.hasAttribute('data-page-content-video')) {
      document.body.removeAttribute('data-page-content-video');
    }
  }, []);

  const handleEventWidget = useCallback(() => {
    const { eventWidget } = data?.items || {};
    if (!eventWidget) {
      return;
    }
    dispatcher.invitation.open(eventWidget);
  }, [data?.items?.eventWidget]);

  const { containerRef: containerPosterRef } = useMediaResource(data?.items?.visual?.poster);

  const { containerRef: containerVisualRef, status: containerVisualStatus } = useMediaResource(data?.items?.visual?.media, useMemo(() => ({
    beforeStart: (target) => {
      setTimeout(() => { handleWaiting(); }, 200);
      target.addEventListener(Video.EVENT.WAITING, handleWaiting);
      target.addEventListener(Video.EVENT.TIME_UPDATE, handleTimeUpdate);
      target.loop(data?.items?.visual?.loop as boolean);
      if (data?.items?.visual?.loop) {
        target.mute();
      }
    },
    afterStop: (target) => {
      target.removeEventListener(Video.EVENT.WAITING, handleWaiting);
      target.removeEventListener(Video.EVENT.TIME_UPDATE, handleTimeUpdate);
      handleTimeUpdate();
    },
    handleMediaEnd: () => {
      if (data?.items?.visual?.media.type === 'video') {
        dispatcher.hint.show();
      }
    },
  }), [data?.items?.visual?.loop, data?.items?.visual?.media.type, handleTimeUpdate, handleWaiting]));

  useEffect(() => {
    if (data?.items?.visual?.media?.type !== 'video') {
      dispatcher.hint.reshow(5000);
    } else {
      dispatcher.hint.hide();
    }
  }, [data?.items?.visual?.media?.type]);

  const width = 320; // * 0.95;
  const height = (width / 2) * 3;

  const bodySize: CSSProperties = useMemo(() => ({
    width: `${width / 10}rem`,
    height: `${height / 10}rem`,
  }), [width, height]);

  const visualStyle: CSSProperties = useMemo(() => ({
    filter: data?.items.visual?.filter,
  }), [data?.items.visual?.filter]);

  const textStyle: CSSProperties = useMemo(() => ({
    fontSize: '1.6em',
    lineHeight: 1.4,
    color: '#181818',
  }), []);

  const eventWidgetStyle = useWidgetPointerStyle(data?.items?.eventWidget?.position);

  return (
    <div className={classes.componentPageContent}>
      <div className={classes.body} style={{ ...bodySize }}>
        {containerVisualStatus === 'error' && (
          <div className={classes.itemText} style={textStyle}>Sorry, something was wrong and this content is unavailable right now.</div>
        )}
        {containerVisualStatus !== 'error' && data?.items?.visual?.media.type === 'video' && data?.items?.visual?.poster && (
          <div className={classes.itemVisualPoster}>
            <div ref={containerPosterRef} />
            <div className={classes.loader}><div /></div>
          </div>
        )}
        {containerVisualStatus !== 'error' && data?.items?.visual && (
          <div className={classes.itemVisual} style={visualStyle} ref={containerVisualRef} data-test="9999" />
        )}
        {containerVisualStatus !== 'error' && data?.items?.visual?.media.type === 'video' && (
          <div className={classes.itemVisualLoader}>
            <div className={classes.loader}><div /></div>
          </div>
        )}
        {!!data?.items?.eventWidget && (
          <div style={eventWidgetStyle} onClick={handleEventWidget} />
        )}
        <ButtonWidget data={data?.items?.buttonWidget} />
        {!!data?.items?.bottomLabel && (
          <div className={classes.bottomLabel}>
            {data.items.bottomLabel}
          </div>
        )}
      </div>
    </div>
  );
};

PageContent.defaultProps = defaultProps;

const useStyles = createUseStyles({
  '@keyframes content-loader': {
    '0%': {
      transform: 'rotate(0deg)',
    },
    '100%': {
      transform: 'rotate(360deg)',
    },
  },
  componentPageContent: {
    flex: 1,
    background: 'white',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  itemVisualPoster: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    display: 'block',
    '& img': {
      filter: 'blur(0.35rem)',
    },
  },
  itemVisualLoader: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    transition: '300ms opacity ease',
    opacity: 0,
  },
  'body[data-page-content-video] .itemVisualLoader': {
    transitionDelay: '100ms',
    opacity: 1,
  },
  itemVisual: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    display: 'flex',
    alignItems: 'center',
    '& > img': {
      display: 'block',
    },
    '& video': {
      transform: 'scale(1.005)',
    },
  },
  itemText: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    display: 'flex',
    padding: '2rem',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
  },
  loader: {
    position: 'absolute',
    display: 'flex',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    alignItems: 'center',
    justifyContent: 'center',
    '& div': {
      borderRadius: '50%',
      width: '3rem',
      height: '3rem',
      fontSize: '1rem',
      position: 'relative',
      border: '.3rem solid rgba(255, 255, 255, 0.75)',
      borderLeftColor: '#26bda5',
      transform: 'translateZ(0)',
      animation: '$content-loader 1.1s linear infinite',
    },
  },
  body: {
    display: 'flex',
    position: 'relative',
    overflow: 'hidden',
    transform: 'translate(0, 0)',
    /* width: '95%', */
    /* borderRadius: '1rem', */
  },
  bottomLabel: {
    position: 'absolute',
    bottom: 0,
    left: '50%',
    transform: 'translate(-50%, 0)',
    background: '#3F3F3F1A',
    color: '#3F3F3F',
    fontSize: '1.2em',
    padding: '0.75em 1em 0.583333333em',
    borderTopLeftRadius: '0.666666667em',
    borderTopRightRadius: '0.666666667em',
    fontWeight: 500,
  },
});

export default memo(PageContent);
